import type { State } from '../configureStore'
import { Changes as ActionTypes } from '../actionTypes'
import * as ReduxHelpers from '@src/utils/reduxHelpers'
import { formatDateTime } from '@src/utils/date.helpers'
import * as changeproto from '@src/endpoints/services/changeMgr.service'
import type { CommonTypeCount } from '@src/endpoints/services/commonHdr.service'
import { defaultHandleStatus, paramsWithFilter } from '@src/endpoints/helpers'
import { parsedChangesList, sortByType } from '@src/utils/changes.utils'
import type {
  ChangeItem,
  Tab,
  TableOptions
} from '../reducers/changes/default.state'
import type { Tenant } from '../reducers/auth'
import { find, toUpper } from 'lodash'
import { getFeatureFlagAsync } from '@src/endpoints/feature.flag'

// Action Creators
export type ActionTypes =
  | getChangesListSuccessAction
  | getChangesListFailAction
  | requestPendingAction
  | disposeChangesAction
  | setTabAction
  | setUseChangesPageAction

export const getChangesListSuccess = (
  data: {
    changes: ChangeItem[]
    counts: CommonTypeCount[]
    pages_count: number
    high_count: number
    medium_count: number
    low_count: number
    total_count: number
  },
  isPending = false
) =>
  ReduxHelpers.Actions.createPayloadAction(
    ActionTypes.GET_CHANGES_LIST_SUCCESS,
    {
      data,
      isPending
    }
  )
type getChangesListSuccessAction = ReturnType<typeof getChangesListSuccess>

export const getChangesListFail = (error: any) =>
  ReduxHelpers.Actions.createPayloadAction(ActionTypes.GET_CHANGES_LIST_FAIL, {
    error,
    isPending: false
  })
type getChangesListFailAction = ReturnType<typeof getChangesListFail>

export const requestPending = (isPending: boolean) =>
  ReduxHelpers.Actions.createPayloadAction(ActionTypes.REQUEST_PENDING, {
    isPending
  })
type requestPendingAction = ReturnType<typeof requestPending>

export const disposeChanges = () =>
  ReduxHelpers.Actions.createAction(ActionTypes.DISPOSE_CHANGES)
type disposeChangesAction = ReturnType<typeof disposeChanges>

export const setTab = (tab: Tab) =>
  ReduxHelpers.Actions.createPayloadAction(ActionTypes.SET_TAB, { tab })
type setTabAction = ReturnType<typeof setTab>

export const setUseChangesPage = (use: boolean) =>
  ReduxHelpers.Actions.createPayloadAction(
    ActionTypes.SET_USE_CHANGES_PAGE_FLAG,
    use
  )
type setUseChangesPageAction = ReturnType<typeof setUseChangesPage>

interface GetChangesListOptions {
  startDate: Date
  endDate: Date
  alertsOnly: boolean
  tableOptions: TableOptions
  tab: Tab
  callback?: (data?: any) => void | null
}

interface GetChangesListParams {
  alerts_only: boolean
  'table_options.page_number': number
  'table_options.page_size': number
  'table_options.sorted_field': string
  'table_options.sorted_asc': boolean
  'table_options.search_filter': string
  'table_options.search_fields': any[]
  resource_type: Tab
  start_date?: string
  end_date?: string
}

export const fetchUseChangesPageFlag =
  () => (dispatch: any, getStore: () => State) => {
    getFeatureFlagAsync('CAM_PERM_20230314_UI_HIDE_CHANGES', {
      signal: null
    }).then(res => {
      dispatch(setUseChangesPage(!res?.value ?? true))
    })
  }

export function getChangesList({
  startDate,
  endDate,
  tableOptions,
  tab,
  alertsOnly,
  callback
}: GetChangesListOptions) {
  return (dispatch: any, getState: () => State) => {
    const { filter, auth } = getState()
    const { tenant } = auth
    const { account_info = [] } = tenant || ({} as Tenant)
    const params: GetChangesListParams = {
      alerts_only: alertsOnly,
      'table_options.page_number': tableOptions.pageNumber,
      'table_options.page_size': tableOptions.pageSize,
      'table_options.sorted_field': tableOptions.sortField,
      'table_options.sorted_asc': tableOptions.asc,
      'table_options.search_filter': tableOptions.searchValue,
      'table_options.search_fields': tableOptions.searchFields,
      resource_type: tab
    }
    if (startDate) {
      params.start_date = formatDateTime(startDate, 'yyyy-MM-dd')
    }
    if (endDate) {
      params.end_date = formatDateTime(endDate, 'yyyy-MM-dd')
    }
    dispatch(requestPending(true))
    changeproto.ChangeMgr.GetChanges({
      params: paramsWithFilter(params, filter)
    })
      .then(res => defaultHandleStatus(res, getChangesListFail, true, dispatch))
      .then(res => {
        const {
          change_info_list = [],
          change_count_list = [],
          pages_count,
          total_count: totals = {} as CommonTypeCount
        } = res || {}
        const changes = parsedChangesList(change_info_list, account_info)
        // filter counts(tabs) to remove UNKNOWN and sort
        const countList = (change_count_list || []).filter(
          item => item.type !== 'UNKNOWN'
        )
        const counts = sortByType(countList)

        const { high_count, medium_count, low_count, total_count } =
          totals || ({} as CommonTypeCount)
        const foundType = find(countList, t => tab === toUpper(t.type))
        if (!foundType) {
          dispatch(requestPending(false))
          dispatch(setTab('ALL'))
        } else {
          dispatch(
            getChangesListSuccess({
              changes,
              pages_count,
              counts,
              high_count,
              medium_count,
              low_count,
              total_count
            })
          )
        }

        if (callback) callback(res)
      })
      .catch((res: Response) => dispatch(getChangesListFail(res?.statusText)))
  }
}
